"use client"

import { ONBOARDING_LANGUAGE_OPTIONS } from "@/lib/onboarding/languages"
import { useEffect, useMemo, type ComponentType, type ComponentProps } from "react"
import { FormikProvider, useFormik } from "formik"
import { Loader2 } from "lucide-react"

import { VoiceSelector } from "@/components/agents/voice/voice-selector"
import { FormTextarea } from "@/components/form-inputs"
import { VoiceLanguageSelector } from "@/components/agents/voice/voice-language-selector"
import { OnboardingStepActions } from "@/components/onboarding/onboarding-step-actions"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { useVoices } from "@/hooks/voice"
import {
  buildVoiceLanguageOptions,
  getVoiceLanguages,
  UNKNOWN_LANGUAGE_VALUE,
} from "@/lib/voice/voice-language-options"
import { useTranslations } from "next-intl"



export type VoiceConfigurationValues = {
  languages: string[]
  voice_id: string
  first_message: string
}

export function SharedVoiceConfigurationForm({
  Actions = OnboardingStepActions,
  languages,
  initialVoiceId = "",
  languageEditing = "enabled",
  voiceAvailability = "automatic",
  openingMessage,
  onBackToPrompt,
  onSubmit,
  submitLabel,
}: {
  Actions?: ComponentType<ComponentProps<typeof OnboardingStepActions>>
  languages: string[]
  initialVoiceId?: string
  languageEditing?: "enabled" | "locked"
  voiceAvailability?: "automatic" | "provider"
  openingMessage: string
  onBackToPrompt: (values: VoiceConfigurationValues) => void
  onSubmit: (values: VoiceConfigurationValues) => Promise<void>
  submitLabel: string
}) {
  const notices = useTranslations("AgentCreationNotices")
  const tUi = useTranslations("Onboarding.VoiceAgentConfigurationForm")
  const { voices, isLoading: voicesLoading, error: voicesError, automaticFallback } = useVoices({ allowAutomaticFallback: voiceAvailability === "automatic" })
  const languageOptions = useMemo(
    () =>
      (automaticFallback ? ONBOARDING_LANGUAGE_OPTIONS : buildVoiceLanguageOptions(voices)).filter(
        (option) => option.value !== UNKNOWN_LANGUAGE_VALUE,
      ),
    [voices, automaticFallback],
  )
  const formik = useFormik<VoiceConfigurationValues>({
    initialValues: {
      languages: languages,
      voice_id: initialVoiceId,
      first_message:
        openingMessage,
    },
    enableReinitialize: true,
    validate: validateVoiceConfiguration,
    onSubmit: async (values, helpers) => {
      helpers.setStatus(undefined)
      try {
        await onSubmit(values)
      } catch (error) {
        helpers.setStatus(error instanceof Error ? error.message : "Failed to save voice settings.")
      }
    },
  })
  const selectedLanguages = formik.values.languages
  const setFieldValue = formik.setFieldValue
  const filteredVoices = useMemo(() => {
    if (selectedLanguages.length === 0) return voices
    return voices.filter((voice) => {
      const supportedLanguages = getVoiceLanguages(voice).map((code) =>
        code.toLowerCase(),
      )
      if (supportedLanguages.length === 0) return true
      return selectedLanguages.every((language) =>
        supportedLanguages.includes(language),
      )
    })
  }, [selectedLanguages, voices])

  useEffect(() => {
    if (languageEditing === "locked" || !languageOptions.length) return
    const available = new Set(languageOptions.map((option) => option.value))
    const validLanguages = selectedLanguages.filter((language) =>
      available.has(language),
    )
    if (validLanguages.length !== selectedLanguages.length) {
      void setFieldValue("languages", validLanguages)
    }
  }, [languageEditing, languageOptions, selectedLanguages, setFieldValue])

  useEffect(() => {
    if (voicesLoading || voicesError) return
    if (filteredVoices.some(voice => voice.id === formik.values.voice_id)) return
    const nextVoiceId = filteredVoices[0]?.id ?? ""
    if (nextVoiceId !== formik.values.voice_id) void setFieldValue("voice_id", nextVoiceId, false)
  }, [filteredVoices, formik.values.voice_id, setFieldValue, voicesLoading, voicesError])

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit} className="space-y-6">
        <div className="rounded-xl border bg-muted/20 p-4 sm:p-5">
          <h2 className="text-lg font-medium"><span key={tUi("chooseHowYourAgentSounds")}>{tUi("chooseHowYourAgentSounds")}</span></h2>
          <p className="mt-1 text-sm text-muted-foreground">
            <span key={tUi("selectLanguagesPreviewTheAvailableVoicesAndSet")}>{tUi("selectLanguagesPreviewTheAvailableVoicesAndSet")}</span></p>
        </div>

        {typeof formik.status === "string" ? (
          <Alert className="border-destructive/40">
            <AlertDescription>{formik.status}</AlertDescription>
          </Alert>
        ) : null}

        <div>
          <VoiceLanguageSelector
            options={languageOptions}
            value={formik.values.languages}
            onChange={(languages) => {
              void formik.setFieldValue("languages", languages)
              void formik.setFieldTouched("languages", true, false)
            }}
            disabled={languageEditing === "locked" || voicesLoading || Boolean(voicesError)}
            loading={voicesLoading}
            error={voicesError}
          />
          {formik.touched.languages && formik.errors.languages ? (
            <p className="mt-1 text-sm text-destructive">
              {formik.errors.languages}
            </p>
          ) : null}
        </div>

        <div>
          {automaticFallback ? (
            <p className="mb-3 text-sm text-muted-foreground">
              <span key={tUi("voiceChoicesUnavailable")}>{tUi("voiceChoicesUnavailable")}</span>
            </p>
          ) : null}
          {!voicesLoading && voices.length > 0 && filteredVoices.length === 0 ? <p role="alert" className="mb-3 text-sm text-destructive"><span>{notices("noCompatibleVoice")}</span></p> : null}
          <VoiceSelector
            voices={filteredVoices}
            selectedVoiceId={formik.values.voice_id}
            onSelect={(voiceId) => {
              void formik.setFieldValue("voice_id", voiceId)
              void formik.setFieldTouched("voice_id", true, false)
            }}
            isLoading={voicesLoading}
            error={voicesError}
          />
          {formik.touched.voice_id && formik.errors.voice_id ? (
            <p className="mt-1 text-sm text-destructive">
              {formik.errors.voice_id}
            </p>
          ) : null}
        </div>

        <FormTextarea
          name="first_message"
          label={tUi("openingGreeting")}
          rows={3}
          maxLength={2000}
          description={tUi("thisIsWhatTheAgentSaysWhenA")}
        />

        <Actions
          backAction={{
            disabled: formik.isSubmitting,
            onClick: () => onBackToPrompt(formik.values),
          }}
        >
          <Button
            type="submit"
            disabled={formik.isSubmitting}
          >
            {formik.isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <span key={tUi("savingVoiceSettings")}>{tUi("savingVoiceSettings")}</span></>
            ) : (
              <span>{submitLabel}</span>
            )}
          </Button>
        </Actions>
      </form>
    </FormikProvider>
  )
}

function validateVoiceConfiguration(
  values: VoiceConfigurationValues,
): Partial<Record<keyof VoiceConfigurationValues, string>> {
  const errors: Partial<Record<keyof VoiceConfigurationValues, string>> = {}
  if (values.languages.length === 0) {
    errors.languages = "Choose at least one language."
  }
  if (!values.voice_id) errors.voice_id = "Choose a voice."
  const greeting = values.first_message.trim()
  if (greeting.length < 5) {
    errors.first_message = "The opening greeting must be at least 5 characters."
  } else if (greeting.length > 2000) {
    errors.first_message =
      "The opening greeting must be 2,000 characters or fewer."
  }
  return errors
}
